import { useContext, useEffect } from 'react';
import { useCompany } from '@extensions/hooks/useCompany';
import { usePageMask } from '@/components';
import { CustomStyleContext } from '@/shared/customStyleButton';
import { GlobalContext } from '@/shared/global';
import { setChannelStoreType } from '@/shared/service/b2b';
import {
  isB2BUserSelector,
  rolePermissionSelector,
  setGlobalCommonState,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import {
  getCurrentCustomerInfo,
  getCompanyInfo,
  loginInfo,
} from '@/utils/loginInfo';
import {
  getStoreTaxZoneRates,
  getTemPlateConfig,
  setStorefrontConfig,
} from '@/utils/storefrontConfig';
import clearInvoiceCart from '@/utils/b3ClearCart';
import {
  getQuoteEnabled,
  handleHideRegisterPage,
  hideStorefrontElement,
  removeBCMenus,
} from '@/utils';

/**
 * @TODO: Documenting tech debt for later, this is a legacy code
 * taken from legacy/apps/storefront/src/App.tsx
 * It was necessary to have, in order to set/retrieve the right info from big commerce.
 * It gets GraphQL token, sets channel data to the store, fetches configs, company info, customer info
 */
export function B2BSessionSetup() {
  const {
    dispatch,
    state: { quoteConfig, storefrontConfig, registerEnabled },
  } = useContext(GlobalContext);
  const { dispatch: styleDispatch } = useContext(CustomStyleContext);

  const showPageMask = usePageMask();

  const isB2BUser = useAppSelector(isB2BUserSelector);
  const isAgenting = useAppSelector(
    ({ b2bFeatures }) => b2bFeatures.masqueradeCompany.isAgenting
  );
  const bcGraphqlToken = useAppSelector(
    ({ company }) => company.tokens.bcGraphqlToken
  );
  const customerId = useAppSelector(({ company }) => company.customer.id);
  const emailAddress = useAppSelector(
    ({ company }) => company.customer.emailAddress
  );
  const role = useAppSelector(({ company }) => company.customer.role);
  const b2bId = useAppSelector(({ company }) => company.customer.b2bId);
  const companyId = useAppSelector(({ company }) => company.companyInfo.id);
  const masqueradeCompanyId = useAppSelector(
    ({ b2bFeatures }) => b2bFeatures.masqueradeCompany.id
  );
  const { quotesCreateActionsPermission, shoppingListCreateActionsPermission } =
    useAppSelector(rolePermissionSelector);
  const storeDispatch = useAppDispatch();

  const { handleCompanyIdChange, clearAllCompanyLocalStorage } = useCompany();

  useEffect(() => {
    removeBCMenus();
  }, []);

  // Core session initialization
  useEffect(() => {
    const init = async () => {
      if (!bcGraphqlToken) {
        await loginInfo();
      }

      setChannelStoreType();

      try {
        await Promise.allSettled([
          getStoreTaxZoneRates(),
          setStorefrontConfig(dispatch),
          getTemPlateConfig(styleDispatch, dispatch),
          getCompanyInfo(role, b2bId),
        ]);
      } catch (e) {
        console.error('[B2BSessionSetup] error:', e);
      }

      const userInfo = {
        role: Number(role),
        isAgenting,
      };

      if (!customerId) {
        const info = await getCurrentCustomerInfo();
        if (info) {
          userInfo.role = info?.role;
        }
      }

      showPageMask(true);

      if (customerId) {
        clearInvoiceCart();
      }

      showPageMask(false);

      storeDispatch(
        setGlobalCommonState({
          isPageComplete: true,
        })
      );
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    b2bId,
    customerId,
    emailAddress,
    isAgenting,
    isB2BUser,
    role,
    bcGraphqlToken,
  ]);

  useEffect(() => {
    if (role !== undefined && typeof isAgenting === 'boolean') {
      handleCompanyIdChange();
    }
  }, [companyId, masqueradeCompanyId, isAgenting, role, handleCompanyIdChange]);

  useEffect(() => {
    if (!customerId) {
      clearAllCompanyLocalStorage();
    }
  }, [customerId, clearAllCompanyLocalStorage]);

  useEffect(() => {
    handleHideRegisterPage(registerEnabled);
  }, [registerEnabled]);

  useEffect(() => {
    if (!quoteConfig.length || !storefrontConfig) return;

    const {
      productQuoteEnabled,
      cartQuoteEnabled,
      shoppingListEnabled,
      registerEnabled: registerEnabledFromQuote,
    } = getQuoteEnabled(
      quoteConfig,
      storefrontConfig,
      role,
      isB2BUser,
      isAgenting
    );

    dispatch({
      type: 'common',
      payload: {
        productQuoteEnabled: isB2BUser
          ? productQuoteEnabled && quotesCreateActionsPermission
          : productQuoteEnabled,
        cartQuoteEnabled: isB2BUser
          ? cartQuoteEnabled && quotesCreateActionsPermission
          : cartQuoteEnabled,
        shoppingListEnabled: isB2BUser
          ? shoppingListEnabled && shoppingListCreateActionsPermission
          : shoppingListEnabled,
        registerEnabled: registerEnabledFromQuote,
      },
    });

    if (isB2BUser) {
      hideStorefrontElement('dom.hideThemePayments');
    }

    setTimeout(() => {
      if (window.b2b?.initializationEnvironment) {
        window.b2b.initializationEnvironment.isInit = true;
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isB2BUser,
    isAgenting,
    role,
    quoteConfig,
    storefrontConfig,
    quotesCreateActionsPermission,
    shoppingListCreateActionsPermission,
  ]);
}